import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Helmet } from 'react-helmet';
import { ObservationLoadingAndFailureIndicators } from 'apps/sigma2-ui/src/components/ObservationLoadingAndFailureIndicators';
import { UserAnalysesOverlay } from 'apps/sigma2-ui/src/components/UserAnalysesOverlay';
import { documentTitle } from 'apps/sigma2-ui/src/lib/document-head';
import { useAuthenticatedPageRedirect } from 'apps/sigma2-ui/src/lib/hooks/useAuthenticatedPageRedirect';
import { useIsLoadingObservation } from 'apps/sigma2-ui/src/lib/hooks/useIsLoadingObservation';
import { useLanguage } from 'apps/sigma2-ui/src/lib/hooks/useLanguage';
import { useObservation } from 'apps/sigma2-ui/src/state/observation';
import { MainLayout } from 'apps/sigma2-ui/src/components/MainLayout';
import { CreateOrEditRubricView } from 'apps/sigma2-ui/src/components/rubrics/views/CreateOrEditRubricView';
import { isEntitledToRubricsManage } from 'libs/end-to-end/src/group-entitlements';
import { isEntitledToRubricsRead } from 'libs/end-to-end/src/group-entitlements';
import { isRubricsPageEnabledForRevision } from 'libs/end-to-end/src/common-revision-flags';
import { useNavigateSafe } from 'apps/sigma2-ui/src/lib/hooks/useNavigateSafe';
import { useBlocker } from 'react-router';
import { useParams } from 'react-router';
import { EditableRubricCategory } from 'libs/end-to-end/src/types/rubrics/rubric';
import { RubricRole } from 'libs/end-to-end/src/types/rubrics/rubric';
import { RubricCategory } from 'libs/end-to-end/src/types/rubrics/rubric';
import { EditableRubricCategoryWithError } from 'libs/end-to-end/src/types/rubrics/rubric';
import { RubricStatus } from 'libs/end-to-end/src/types/rubrics/rubric';
import { EditableRubricRole } from 'libs/end-to-end/src/types/rubrics/rubric';
import { EditableRubricRoleWithError } from 'libs/end-to-end/src/types/rubrics/rubric';
import { RubricGradingType } from 'libs/end-to-end/src/types/rubrics/rubric';
import { RubricRoleOperation } from 'libs/end-to-end/src/types/rubrics/rubric';
import { RubricCategoryModalRowProps } from 'apps/sigma2-ui/src/components/rubrics/RubricModals';
import { RubricRoleModalRowProps } from 'apps/sigma2-ui/src/components/rubrics/RubricModals';
import { clientCreateRubric } from 'apps/sigma2-ui/src/components/rubrics/rubric-utils';
import { clientGetIntegrationDataSets } from 'apps/sigma2-ui/src/components/rubrics/rubric-utils';
import { clientUpdateRubric } from 'apps/sigma2-ui/src/components/rubrics/rubric-utils';
import { clientUpdateRubricStatus } from 'apps/sigma2-ui/src/components/rubrics/rubric-utils';
import { clientGetRubricCategories } from 'apps/sigma2-ui/src/components/rubrics/rubric-utils';
import { clientGetRubricTags } from 'apps/sigma2-ui/src/components/rubrics/rubric-utils';
import { clientGetRubricDetails } from 'apps/sigma2-ui/src/components/rubrics/rubric-utils';
import { clientGetRubricRoles } from 'apps/sigma2-ui/src/components/rubrics/rubric-utils';
import { useUserObservationOrgAndDbIds } from 'apps/sigma2-ui/src/lib/hooks/useUserObservationOrgAndDbIds';
import { CategoryListItemDto } from 'libs/end-to-end/src/rubrics';
import { RubricDetailsDto } from 'libs/end-to-end/src/rubrics';
import { CreateOrReferenceCategoryForWrite } from 'libs/end-to-end/src/rubrics';
import { CreateOrReferenceRubricRoleForWrite } from 'libs/end-to-end/src/rubrics';
import { RoleListItemDto } from 'libs/end-to-end/src/rubrics';
import { useUser } from 'apps/sigma2-ui/src/state/user';
import { useAlive } from 'apps/sigma2-ui/src/lib/hooks/useAlive';
import { DataSet } from 'libs/end-to-end/src/types/databases/data-sets/data-set';
import { UnsavedChangesModal } from 'apps/sigma2-ui/src/components/dashboards/UnsavedChangesModal';

export interface CreateRubricPageProps {
  mode: 'create' | 'edit' | 'duplicate';
  gradingTypeParam?: string;
  rubricIdParam?: string;
}

export const CreateRubricPage: React.FC<CreateRubricPageProps> = ({
  mode,
  rubricIdParam,
  gradingTypeParam,
}) => {
  const { myObservation } = useObservation();
  const { userAuthenticatedApiCall } = useUser();
  const language = useLanguage();
  const navigate = useNavigateSafe();

  const {
    selectedOrganizationId,
    selectedDatabaseId,
    selectedDatabaseRevisionId,
  } = useUserObservationOrgAndDbIds();

  const [saveError, setSaveError] = useState<string | null>(null);
  const hasLoadedTagLibraryRef = useRef(false);
  const [errorLoadingTagLibrary, setErrorLoadingTagLibrary] = useState<
    string | null
  >(null);
  const [isLoadingTagLibrary, setIsLoadingTagLibrary] = useState(false);
  const [possibleTags, setPossibleTags] = useState<string[]>([]);

  const hasLoadedRoleLibraryRef = useRef(false);
  const [errorLoadingRoleLibrary, setErrorLoadingRoleLibrary] = useState<
    string | null
  >(null);
  const [isLoadingRoleLibrary, setIsLoadingRoleLibrary] = useState(false);
  const [roleLibrary, setRoleLibrary] = useState<RoleListItemDto[]>([]);

  const hasLoadedCategoryLibraryRef = useRef(false);
  const [errorLoadingCategoryLibrary, setErrorLoadingCategoryLibrary] =
    useState<string | null>(null);
  const [isLoadingCategoryLibrary, setIsLoadingCategoryLibrary] =
    useState(false);

  const [categoryLibrary, setCategoryLibrary] = useState<CategoryListItemDto[]>(
    [],
  );
  const [gradingType, setGradingType] = useState<RubricGradingType | null>(
    null,
  );

  const [status, setStatus] = useState<RubricStatus>('draft');
  const [name, setName] = useState('');
  const [savedName, setSavedName] = useState('');
  const [description, setDescription] = useState('');
  const [dataSetId, setDataSetId] = useState('');

  const [createdAt, setCreatedAt] = useState<Date>();
  const [updatedAt, setUpdatedAt] = useState<Date>();

  const [roles, setRoles] = useState<EditableRubricRole[]>([]);
  const [isRoleLibraryOpen, setIsRoleLibraryOpen] = useState(false);
  const [isCategoryLibraryOpen, setIsCategoryLibraryOpen] = useState(false);
  const [categories, setCategories] = useState<EditableRubricCategory[]>([]);

  const hasLoadedExistingRubricRef = useRef(false);
  const [errorLoadingExistingRubric, setErrorLoadingExistingRubric] = useState<
    string | null
  >(null);
  const [isLoadingExistingRubric, setIsLoadingExistingRubric] = useState(false);

  const hasLoadedDataSetsRef = useRef(false);
  const [errorLoadingDataSets, setErrorLoadingDataSets] = useState<
    string | null
  >(null);
  const [isLoadingDataSets, setIsLoadingDataSets] = useState(false);
  const [possibleDataSets, setPossibleDataSets] = useState<DataSet[]>([]);
  const [isDirty, setIsDirty] = useState(false);
  const isDirtyRef = useRef(false);
  const setDirty = useCallback((value: boolean) => {
    isDirtyRef.current = value;
    setIsDirty(value);
  }, []);

  const serverError = useMemo(
    () =>
      saveError ||
      errorLoadingCategoryLibrary ||
      errorLoadingDataSets ||
      errorLoadingExistingRubric ||
      errorLoadingRoleLibrary ||
      errorLoadingTagLibrary,
    [
      errorLoadingCategoryLibrary,
      errorLoadingDataSets,
      errorLoadingExistingRubric,
      errorLoadingRoleLibrary,
      errorLoadingTagLibrary,
      saveError,
    ],
  );

  const isAlive = useAlive();

  const roleLibraryList: RubricRoleModalRowProps[] = useMemo(() => {
    return roleLibrary.map((r) => {
      return {
        name: r.name,
        useTypes: {
          resume: r.gradingTypes.includes('resume'),
          interview: r.gradingTypes.includes('interview'),
          questions: r.gradingTypes.includes('questions'),
        },
        liveUses: r.rubricCount,
      };
    });
  }, [roleLibrary]);

  const categoryLibraryList: RubricCategoryModalRowProps[] = useMemo(() => {
    return categoryLibrary.map((cat) => {
      return {
        label: cat.label,
        tags: cat.tags,
        criteria: cat.criteria.map((crit) => {
          return {
            criteria: crit.criteria,
            weight: crit.weight,
          };
        }),
        id: cat.id,
      };
    });
  }, [categoryLibrary]);

  const applyChangesFromDto = useCallback(
    ({
      rubricDto,
      duplicateMode,
    }: {
      rubricDto: RubricDetailsDto;
      duplicateMode: boolean;
    }) => {
      setSaveError(null);
      setGradingType(rubricDto.gradingType);
      setName(`${rubricDto.name}${duplicateMode ? ' (Copy)' : ''}`);
      setSavedName(`${duplicateMode ? '' : rubricDto.name}`);
      setCreatedAt(duplicateMode ? undefined : rubricDto.createdAt);
      setUpdatedAt(duplicateMode ? undefined : rubricDto.updatedAt);
      setDescription(rubricDto.description);
      setStatus(duplicateMode ? 'draft' : rubricDto.status);
      setDataSetId(rubricDto.integrationDataSet.id);
      setRoles(
        rubricDto.roles.map((r) => {
          return {
            record: {
              name: r.role.name,
              roleOperation: r.roleOperation,
            },
            draft: null,
          };
        }),
      );
      setCategories(
        rubricDto.categories.map((cat) => {
          return {
            record: {
              label: cat.label,
              tags: cat.categoryTags.map((t) => t.tag.name),
              criteria: cat.categoryCriteria.map((crit) => {
                return {
                  text: crit.criteria,
                  weight: crit.weight,
                };
              }),
            },
            draft: null,
            // When duplicating a rubric, the categories are not duplicated, so we don't need to set the id.
            id: duplicateMode ? undefined : cat.id,
          };
        }),
      );
    },
    [],
  );

  // This effect loads the existing rubric (if any) by param ID on page load.
  useEffect(() => {
    if (
      !myObservation ||
      !selectedOrganizationId ||
      !selectedDatabaseId ||
      !selectedDatabaseRevisionId ||
      hasLoadedExistingRubricRef.current
    ) {
      return;
    }

    hasLoadedExistingRubricRef.current = true;

    if (rubricIdParam) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setIsLoadingExistingRubric(true);
      clientGetRubricDetails({
        organizationId: selectedOrganizationId,
        databaseId: selectedDatabaseId,
        databaseRevisionId: selectedDatabaseRevisionId,
        rubricId: rubricIdParam,
        onSuccess: (rubricDto) => {
          setIsLoadingExistingRubric(false);
          setErrorLoadingExistingRubric(null);

          applyChangesFromDto({
            rubricDto,
            duplicateMode: mode === 'duplicate',
          });
        },
        onError: (error) => {
          setIsLoadingExistingRubric(false);
          setErrorLoadingExistingRubric(error);
        },
        isAlive,
        userAuthenticatedApiCall,
      });
    } else {
      const parsedGradingType = RubricGradingType.safeParse(gradingTypeParam);

      if (parsedGradingType.success) {
        setGradingType(parsedGradingType.data);
      }
    }
  }, [
    myObservation,
    userAuthenticatedApiCall,
    isAlive,
    selectedOrganizationId,
    selectedDatabaseId,
    selectedDatabaseRevisionId,
    rubricIdParam,
    language,
    mode,
    gradingTypeParam,
    applyChangesFromDto,
  ]);

  // This effect loads the tag library on page load.
  useEffect(() => {
    if (
      !myObservation ||
      !selectedOrganizationId ||
      !selectedDatabaseId ||
      !selectedDatabaseRevisionId ||
      hasLoadedTagLibraryRef.current
    ) {
      return;
    }
    hasLoadedTagLibraryRef.current = true;

    // eslint-disable-next-line react-hooks/set-state-in-effect
    setIsLoadingTagLibrary(true);
    clientGetRubricTags({
      organizationId: selectedOrganizationId,
      databaseId: selectedDatabaseId,
      databaseRevisionId: selectedDatabaseRevisionId,
      onSuccess: (newTags) => {
        setPossibleTags(newTags.map((t) => t.name));
        setIsLoadingTagLibrary(false);
        setErrorLoadingTagLibrary(null);
      },
      onError: (error) => {
        setIsLoadingTagLibrary(false);
        setErrorLoadingTagLibrary(error);
      },
      isAlive,
      userAuthenticatedApiCall,
    });
  }, [
    myObservation,
    userAuthenticatedApiCall,
    isAlive,
    selectedOrganizationId,
    selectedDatabaseId,
    selectedDatabaseRevisionId,
  ]);

  // This effect loads the role library on page load.
  useEffect(() => {
    if (
      !myObservation ||
      !selectedOrganizationId ||
      !selectedDatabaseId ||
      !selectedDatabaseRevisionId ||
      hasLoadedRoleLibraryRef.current
    ) {
      return;
    }
    hasLoadedRoleLibraryRef.current = true;

    // eslint-disable-next-line react-hooks/set-state-in-effect
    setIsLoadingRoleLibrary(true);

    clientGetRubricRoles({
      organizationId: selectedOrganizationId,
      databaseId: selectedDatabaseId,
      databaseRevisionId: selectedDatabaseRevisionId,
      onSuccess: (newRoles) => {
        setRoleLibrary(newRoles);
        setIsLoadingRoleLibrary(false);
        setErrorLoadingRoleLibrary(null);
      },
      onError: (error) => {
        setIsLoadingRoleLibrary(false);
        setErrorLoadingRoleLibrary(error);
      },
      isAlive,
      userAuthenticatedApiCall,
    });
  }, [
    myObservation,
    userAuthenticatedApiCall,
    isAlive,
    selectedOrganizationId,
    selectedDatabaseId,
    selectedDatabaseRevisionId,
  ]);

  // This effect loads the category library on page load.
  useEffect(() => {
    if (
      !myObservation ||
      !selectedOrganizationId ||
      !selectedDatabaseId ||
      !selectedDatabaseRevisionId ||
      !gradingType ||
      hasLoadedCategoryLibraryRef.current
    ) {
      return;
    }
    hasLoadedCategoryLibraryRef.current = true;

    // eslint-disable-next-line react-hooks/set-state-in-effect
    setIsLoadingCategoryLibrary(true);

    clientGetRubricCategories({
      organizationId: selectedOrganizationId,
      databaseId: selectedDatabaseId,
      databaseRevisionId: selectedDatabaseRevisionId,
      onSuccess: (newCategories) => {
        setCategoryLibrary(newCategories);
        setIsLoadingCategoryLibrary(false);
        setErrorLoadingCategoryLibrary(null);
      },
      onError: (error) => {
        setIsLoadingCategoryLibrary(false);
        setErrorLoadingCategoryLibrary(error);
      },
      isAlive,
      userAuthenticatedApiCall,
      gradingType,
    });
  }, [
    myObservation,
    userAuthenticatedApiCall,
    isAlive,
    selectedOrganizationId,
    selectedDatabaseId,
    selectedDatabaseRevisionId,
    gradingType,
  ]);

  // This effect loads the data sets on page load.
  useEffect(() => {
    if (
      !myObservation ||
      !selectedOrganizationId ||
      hasLoadedDataSetsRef.current
    ) {
      return;
    }
    hasLoadedDataSetsRef.current = true;

    // eslint-disable-next-line react-hooks/set-state-in-effect
    setIsLoadingDataSets(true);

    clientGetIntegrationDataSets({
      organizationId: selectedOrganizationId,
      onSuccess: (newDataSets) => {
        setPossibleDataSets(newDataSets);
        setIsLoadingDataSets(false);
        setErrorLoadingDataSets(null);
      },
      onError: (error) => {
        setIsLoadingDataSets(false);
        setErrorLoadingDataSets(error);
      },
      isAlive,
      userAuthenticatedApiCall,
    });
  }, [
    myObservation,
    userAuthenticatedApiCall,
    isAlive,
    selectedOrganizationId,
  ]);

  useAuthenticatedPageRedirect();

  const { isLoadingObservation: isLoadingStill, failedToLoadObservation } =
    useIsLoadingObservation();

  const isArchiveMode = useMemo(() => {
    return status === 'archived';
  }, [status]);

  const rubricAccessLevel = useMemo(() => {
    if (isLoadingStill || !myObservation) {
      return 'loading';
    }

    if (!isRubricsPageEnabledForRevision({ revisionFlags: myObservation })) {
      return 'none';
    }

    const entitledManage = isEntitledToRubricsManage({
      features: myObservation.features,
    });

    if (entitledManage) {
      return 'manage';
    }

    const entitledRead = isEntitledToRubricsRead({
      features: myObservation.features,
    });

    if (entitledRead) {
      return 'read';
    }

    return 'none';
  }, [isLoadingStill, myObservation]);

  useEffect(() => {
    if (rubricAccessLevel === 'none') {
      navigate('/overview');
    }
  }, [rubricAccessLevel, navigate]);

  const isReadOnlyMode = useMemo(() => {
    return isArchiveMode || rubricAccessLevel === 'read';
  }, [isArchiveMode, rubricAccessLevel]);

  const yScrollRef = useRef<HTMLDivElement>(null);

  const setCategoryIntoDraftMode = useCallback((categoryIndex: number) => {
    setCategories((prev) =>
      prev.map((category, i) => {
        if (i !== categoryIndex || category.draft || !category.record) {
          return category;
        }

        return {
          ...category,
          draft: {
            ...category.record,
            tags: [...category.record.tags],
            criteria: category.record.criteria.map((crit) => ({ ...crit })),
          },
        };
      }),
    );
  }, []);

  const getCategoryErrorLocationFromServerError = useCallback(
    (error?: string | null) => {
      if (!error) {
        return null;
      }

      const nameMatch = error.match(/^CATEGORY_NAME_DUPLICATE-(\d+)$/);
      if (nameMatch) {
        const categoryIndex = Number(nameMatch[1]);
        return Number.isNaN(categoryIndex)
          ? null
          : {
              categoryIndex,
              criteriaIndex: null,
              kind: 'name' as const,
            };
      }

      const criteriaMatch = error.match(
        /^CATEGORY_CRITERIA_DUPLICATE-(\d+)-(\d+)$/,
      );
      if (criteriaMatch) {
        const categoryIndex = Number(criteriaMatch[1]);
        const criteriaIndex = Number(criteriaMatch[2]);

        if (Number.isNaN(categoryIndex) || Number.isNaN(criteriaIndex)) {
          return null;
        }

        return {
          categoryIndex,
          criteriaIndex,
          kind: 'criteria' as const,
        };
      }

      return null;
    },
    [],
  );

  const handleNameChange = useCallback(
    (newName: string) => {
      setDirty(true);
      setName(newName);
    },
    [setDirty],
  );

  const handleDescriptionChange = useCallback(
    (newDescription: string) => {
      setDirty(true);
      setDescription(newDescription);
    },
    [setDirty],
  );

  const handleDataSetIdChange = useCallback(
    (newDataSetId: string) => {
      setDirty(true);
      setDataSetId(newDataSetId);
    },
    [setDirty],
  );

  const handleRoleNameChanged = useCallback(
    (index: number, name: string) => {
      setDirty(true);

      setRoles((prev) =>
        prev.map((p, i) => {
          if (index !== i || !p.draft) return p;

          return {
            ...p,
            draft: {
              ...p.draft,
              name,
            },
          };
        }),
      );
    },
    [setDirty],
  );

  const handleRoleOperationChanged = useCallback(
    (index: number, roleOperation: RubricRoleOperation) => {
      setDirty(true);

      setRoles((prev) =>
        prev.map((p, i) => {
          if (index !== i || !p.draft) return p;

          return {
            ...p,
            draft: {
              ...p.draft,
              roleOperation,
            },
          };
        }),
      );
    },
    [setDirty],
  );

  const handleRoleSaved = useCallback(
    (index: number) => {
      setDirty(true);

      setRoles((prev) =>
        prev.map((p, i) => {
          if (index !== i || !p.draft) return p;

          return {
            record: p.draft,
            draft: null,
          };
        }),
      );
    },
    [setDirty],
  );

  const handleRoleCancelled = useCallback((index: number) => {
    setRoles((prev) => {
      const next = prev.map((p, i) => {
        if (index !== i || !p.draft) return p;

        return {
          ...p,
          draft: null,
        };
      });

      return next.filter((r, i) => (index !== i ? true : !!r.record));
    });
  }, []);

  const handleRoleBeginEdit = useCallback(
    (index: number) => {
      setDirty(true);

      setRoles((prev) =>
        prev.map((p, i) => {
          if (index !== i || p.draft || !p.record) return p;

          return {
            ...p,
            draft: p.record,
          };
        }),
      );
    },
    [setDirty],
  );

  const handleRoleRemove = useCallback(
    (index: number) => {
      setDirty(true);

      setRoles((prev) =>
        prev.filter((_, i) => {
          return index !== i;
        }),
      );
    },
    [setDirty],
  );

  const handleRoleCreate = useCallback(() => {
    setDirty(true);

    setRoles((prev) => [
      ...prev,
      {
        draft: {
          name: '',
          roleOperation: 'isEqualTo',
        },
        record: null,
      },
    ]);
  }, [setDirty]);

  const getErrorsForRole = useCallback(
    (role: RubricRole, roleIndex: number) => {
      let requiredRoleError = false;
      let duplicateRoleError = false;

      if (role.name.trim() === '') {
        requiredRoleError = true;
      }

      const nameInUse = roles.some(
        (r, i) =>
          r.record &&
          roleIndex !== i &&
          r.record.name === role.name &&
          r.record.roleOperation === role.roleOperation,
      );

      if (nameInUse) {
        duplicateRoleError = true;
      }

      return {
        requiredRoleError,
        duplicateRoleError,
      };
    },
    [roles],
  );

  const getErrorsForCategory = useCallback(
    (category: RubricCategory, categoryIndex: number) => {
      let requiredLabelError = false;
      let duplicateLabelError = false;

      if (category.label.trim() === '') {
        requiredLabelError = true;
      }

      const nameInUse = categories.some(
        (cat, i) =>
          cat.record &&
          categoryIndex !== i &&
          cat.record.label === category.label,
      );

      if (nameInUse) {
        duplicateLabelError = true;
      }

      const requiredCriteriaErrors: number[] = [];

      for (
        let criteriaIndex = 0;
        criteriaIndex < category.criteria.length;
        ++criteriaIndex
      ) {
        const criteria = category.criteria[criteriaIndex];
        if (!criteria.text) {
          requiredCriteriaErrors.push(criteriaIndex);
        }
      }

      return {
        requiredLabelError,
        duplicateLabelError,
        requiredCriteriaErrors,
      };
    },
    [categories],
  );

  const rolesWithErrors = useMemo((): EditableRubricRoleWithError[] => {
    const result: EditableRubricRoleWithError[] = [];
    for (let roleIndex = 0; roleIndex < roles.length; ++roleIndex) {
      const role = roles[roleIndex];

      if (role.draft) {
        const { requiredRoleError, duplicateRoleError } = getErrorsForRole(
          role.draft,
          roleIndex,
        );

        result.push({
          ...role,
          requiredRoleError,
          duplicateRoleError,
        });
      } else if (role.record) {
        const { requiredRoleError, duplicateRoleError } = getErrorsForRole(
          role.record,
          roleIndex,
        );

        result.push({
          ...role,
          requiredRoleError,
          duplicateRoleError,
        });
      }
    }

    return result;
  }, [getErrorsForRole, roles]);

  const categoriesWithErrors =
    useMemo((): EditableRubricCategoryWithError[] => {
      const result: EditableRubricCategoryWithError[] = [];
      for (
        let categoryIndex = 0;
        categoryIndex < categories.length;
        ++categoryIndex
      ) {
        const category = categories[categoryIndex];

        if (category.draft) {
          const {
            requiredCriteriaErrors,
            requiredLabelError,
            duplicateLabelError,
          } = getErrorsForCategory(category.draft, categoryIndex);

          result.push({
            ...category,
            requiredCriteriaErrors,
            requiredLabelError,
            duplicateLabelError,
            duplicateLabelServerError:
              saveError === `CATEGORY_NAME_DUPLICATE-${categoryIndex}`,
          });
        } else if (category.record) {
          const {
            requiredCriteriaErrors,
            requiredLabelError,
            duplicateLabelError,
          } = getErrorsForCategory(category.record, categoryIndex);

          result.push({
            ...category,
            requiredCriteriaErrors,
            requiredLabelError,
            duplicateLabelError,
            duplicateLabelServerError:
              saveError === `CATEGORY_NAME_DUPLICATE-${categoryIndex}`,
          });
        }
      }

      return result;
    }, [categories, getErrorsForCategory, saveError]);

  const handleAddSelectedRoles = useCallback(
    (checkedRoles: string[]) => {
      setDirty(true);

      setRoles((prev) => [
        ...prev,
        ...checkedRoles.map((name) => ({
          record: null,
          draft: {
            name,
            roleOperation: 'isEqualTo' as const,
          },
        })),
      ]);
      setIsRoleLibraryOpen(false);
    },
    [setDirty],
  );

  const handleDismissRoleLibrary = useCallback(() => {
    setIsRoleLibraryOpen(false);
  }, []);

  const handleDismissCategoryLibrary = useCallback(() => {
    setIsCategoryLibraryOpen(false);
  }, []);

  const handleRoleSelect = useCallback(() => {
    setIsRoleLibraryOpen(true);
  }, []);

  const isLoadingObservationOrRubricStill = useMemo(
    () => isLoadingStill || isLoadingExistingRubric,
    [isLoadingStill, isLoadingExistingRubric],
  );

  const handleCategoryStartEdit = useCallback(
    (categoryIndex: number) => {
      setDirty(true);

      setCategories((prev) =>
        prev.map((c, i) => {
          if (categoryIndex !== i || c.draft || !c.record) return c;

          return {
            ...c,
            draft: c.record,
          };
        }),
      );
    },
    [setDirty],
  );

  const handleCategoryRemove = useCallback(
    (categoryIndex: number) => {
      setDirty(true);

      setCategories((prev) =>
        prev.filter((_, i) => {
          return categoryIndex !== i;
        }),
      );
    },
    [setDirty],
  );

  const handleCategoryDuplicate = useCallback(
    (categoryIndex: number) => {
      setDirty(true);

      setCategories((prev) => {
        const item = prev[categoryIndex];

        return [
          ...prev,
          {
            draft: item.record ? { ...item.record } : item.draft,
            record: item.record,
          },
        ];
      });
    },
    [setDirty],
  );

  const handleCategoryCancel = useCallback((categoryIndex: number) => {
    setCategories((prev) => {
      const next = prev.map((p, i) => {
        if (categoryIndex !== i || !p.draft) return p;

        return {
          ...p,
          draft: null,
        };
      });

      return next.filter((r, i) => (categoryIndex !== i ? true : !!r.record));
    });
  }, []);

  const handleCategorySave = useCallback(
    (categoryIndex: number) => {
      setDirty(true);

      setCategories((prev) =>
        prev.map((p, i) => {
          if (categoryIndex !== i || !p.draft) return p;

          return {
            ...p,
            record: p.draft,
            draft: null,
          };
        }),
      );
    },
    [setDirty],
  );

  const handleCategoryUpdateLabel = useCallback(
    (categoryIndex: number, newLabel: string) => {
      setDirty(true);

      setCategories((prev) =>
        prev.map((c, i) => {
          if (i !== categoryIndex || !c.draft) return c;

          return {
            ...c,
            draft: {
              ...c.draft,
              label: newLabel,
            },
          };
        }),
      );
    },
    [setDirty],
  );
  const handleCategoryAddTag = useCallback(
    (categoryIndex: number, newTag: string) => {
      setDirty(true);

      setCategories((prev) =>
        prev.map((c, i) => {
          if (i !== categoryIndex || !c.draft) return c;

          return {
            ...c,
            draft: {
              ...c.draft,
              tags: c.draft.tags.includes(newTag)
                ? c.draft.tags
                : [...c.draft.tags, newTag],
            },
          };
        }),
      );
    },
    [setDirty],
  );
  const handleCategoryRemoveTag = useCallback(
    (categoryIndex: number, removeTag: string) => {
      setDirty(true);

      setCategories((prev) =>
        prev.map((c, i) => {
          if (i !== categoryIndex || !c.draft) return c;

          return {
            ...c,
            draft: {
              ...c.draft,
              tags: c.draft.tags.filter((t) => t !== removeTag),
            },
          };
        }),
      );
    },
    [setDirty],
  );
  const handleCategoryUpdateCriteria = useCallback(
    (categoryIndex: number, criteriaIndex: number, newCriteria: string) => {
      setDirty(true);

      setCategories((prev) =>
        prev.map((c, i) => {
          if (i !== categoryIndex || !c.draft) return c;

          return {
            ...c,
            draft: {
              ...c.draft,
              criteria: c.draft.criteria.map((crit, j) => {
                if (j !== criteriaIndex) return crit;

                return {
                  ...crit,
                  text: newCriteria,
                };
              }),
            },
          };
        }),
      );
    },
    [setDirty],
  );
  const handleCategoryUpdateWeight = useCallback(
    (categoryIndex: number, criteriaIndex: number, newWeight: number) => {
      setDirty(true);

      setCategories((prev) =>
        prev.map((c, i) => {
          if (i !== categoryIndex || !c.draft) return c;

          return {
            ...c,
            draft: {
              ...c.draft,
              criteria: c.draft.criteria.map((crit, j) => {
                if (j !== criteriaIndex) return crit;

                return {
                  ...crit,
                  weight: newWeight,
                };
              }),
            },
          };
        }),
      );
    },
    [setDirty],
  );
  const handleCategoryRemoveCriteria = useCallback(
    (categoryIndex: number, criteriaIndex: number) => {
      setDirty(true);

      setCategories((prev) =>
        prev.map((c, i) => {
          if (i !== categoryIndex || !c.draft) return c;

          return {
            ...c,
            draft: {
              ...c.draft,
              criteria: c.draft.criteria.filter((_, j) => j !== criteriaIndex),
            },
          };
        }),
      );
    },
    [setDirty],
  );
  const handleCategoryAddCriteria = useCallback(
    (categoryIndex: number) => {
      setDirty(true);

      setCategories((prev) =>
        prev.map((c, i) => {
          if (i !== categoryIndex || !c.draft) return c;

          return {
            ...c,
            draft: {
              ...c.draft,
              criteria: [...c.draft.criteria, { text: '', weight: 1 }],
            },
          };
        }),
      );
    },
    [setDirty],
  );

  const handleAddSelectedCategories = useCallback(
    (checkedCategoryIds: string[]) => {
      setDirty(true);

      const newCategories: EditableRubricCategory[] = [];

      for (const checkedCategoryId of checkedCategoryIds) {
        const item = categoryLibrary.find((c) => c.id === checkedCategoryId);

        if (!item) {
          continue;
        }

        newCategories.push({
          record: null,
          draft: {
            label: item.label,
            tags: item.tags,
            criteria: item.criteria.map((crit) => {
              return {
                text: crit.criteria,
                weight: crit.weight,
              };
            }),
          },
        });
      }

      setCategories((prev) => [...prev, ...newCategories]);
      setIsCategoryLibraryOpen(false);
    },
    [categoryLibrary, setDirty],
  );

  const handleCategoryCreate = useCallback(() => {
    setDirty(true);

    setCategories((prev) => [
      ...prev,
      {
        draft: { label: '', tags: [], criteria: [] },
        record: null,
      },
    ]);
  }, [setDirty]);

  const handleCategorySelect = useCallback(() => {
    setIsCategoryLibraryOpen(true);
  }, []);

  const [isSavingRubric, setIsSavingRubric] = useState(false);

  const disableSave = useMemo(() => {
    if (isSavingRubric) {
      return true;
    }

    if (name.trim() === '' || !gradingType) {
      return true;
    }

    if (dataSetId.trim() === '') {
      return true;
    }

    if (categories.length === 0) {
      return true;
    }

    if (roles.length === 0) {
      return true;
    }

    if (roles.some((r) => r.draft)) {
      return true;
    }

    if (
      rolesWithErrors.some((r) => r.duplicateRoleError || r.requiredRoleError)
    ) {
      return true;
    }

    if (categories.some((c) => c.draft)) {
      return true;
    }

    if (
      categoriesWithErrors.some(
        (c) =>
          c.requiredLabelError ||
          c.duplicateLabelError ||
          c.requiredCriteriaErrors.length !== 0,
      )
    ) {
      return true;
    }

    if (!gradingType) {
      return true;
    }

    return false;
  }, [
    isSavingRubric,
    name,
    gradingType,
    dataSetId,
    roles,
    rolesWithErrors,
    categories,
    categoriesWithErrors,
  ]);

  const saveRequestDetails = useMemo(() => {
    if (
      !selectedOrganizationId ||
      !selectedDatabaseId ||
      !selectedDatabaseRevisionId ||
      !gradingType
    ) {
      return {
        requestCategories: [],
        requestRoles: [],
      };
    }

    const requestRoles: CreateOrReferenceRubricRoleForWrite[] = roles.map(
      (role, roleIndex) => {
        return {
          position: roleIndex + 1,
          roleOperation: role.record?.roleOperation ?? 'isEqualTo',
          role: {
            name: role.record?.name ?? '',
          },
        };
      },
    );

    const requestCategories: CreateOrReferenceCategoryForWrite[] =
      categories.map((cat, catIndex) => {
        return {
          label: cat.record?.label ?? '',
          position: catIndex + 1,
          categoryTags:
            cat.record?.tags.map((tag, tagIndex) => {
              return {
                position: tagIndex + 1,
                tag: {
                  name: tag,
                },
              };
            }) ?? [],
          categoryCriteria:
            cat.record?.criteria.map((crit, critIndex) => {
              return {
                position: critIndex + 1,
                weight: crit.weight,
                criteria: crit.text,
              };
            }) ?? [],
          id: cat.id,
        };
      });

    return {
      requestCategories,
      requestRoles,
    };
  }, [
    categories,
    gradingType,
    roles,
    selectedDatabaseId,
    selectedDatabaseRevisionId,
    selectedOrganizationId,
  ]);

  const handleSaveDraft = useCallback(() => {
    if (
      !selectedOrganizationId ||
      !selectedDatabaseId ||
      !selectedDatabaseRevisionId ||
      !gradingType
    ) {
      return;
    }

    const { requestCategories, requestRoles } = saveRequestDetails;

    if (mode === 'edit') {
      if (rubricIdParam) {
        setIsSavingRubric(true);

        clientUpdateRubric({
          isAlive,
          userAuthenticatedApiCall,
          onSuccess: (dto) => {
            setIsSavingRubric(false);
            setDirty(false);

            applyChangesFromDto({
              rubricDto: dto,
              duplicateMode: false,
            });
          },
          onError: (newError) => {
            setIsSavingRubric(false);

            const location = getCategoryErrorLocationFromServerError(newError);
            if (location) {
              setCategoryIntoDraftMode(location.categoryIndex);
            }

            setSaveError(newError ?? 'Unknown error');
          },
          rubricId: rubricIdParam,
          organizationId: selectedOrganizationId,
          databaseId: selectedDatabaseId,
          databaseRevisionId: selectedDatabaseRevisionId,
          rubric: {
            name,
            description,
            integrationDataSetId: dataSetId,
            roles: requestRoles,
            categories: requestCategories,
          },
        });
      }
    } else {
      setIsSavingRubric(true);

      clientCreateRubric({
        isAlive,
        userAuthenticatedApiCall,
        onSuccess: (newDto) => {
          setDirty(false);
          navigate(`/rubrics/edit/${newDto.id}?success=1`);
        },
        onError: (newError) => {
          setIsSavingRubric(false);
          const location = getCategoryErrorLocationFromServerError(newError);
          if (location) {
            setCategoryIntoDraftMode(location.categoryIndex);
          }
          setSaveError(newError ?? 'Unknown error');
        },
        organizationId: selectedOrganizationId,
        databaseId: selectedDatabaseId,
        databaseRevisionId: selectedDatabaseRevisionId,
        rubric: {
          name,
          description,
          integrationDataSetId: dataSetId,
          roles: requestRoles,
          categories: requestCategories,
          gradingType,
        },
      });
    }
  }, [
    applyChangesFromDto,
    dataSetId,
    description,
    getCategoryErrorLocationFromServerError,
    gradingType,
    isAlive,
    mode,
    name,
    navigate,
    rubricIdParam,
    saveRequestDetails,
    selectedDatabaseId,
    selectedDatabaseRevisionId,
    selectedOrganizationId,
    setCategoryIntoDraftMode,
    setDirty,
    userAuthenticatedApiCall,
  ]);

  const handlePublish = useCallback(() => {
    if (
      !selectedOrganizationId ||
      !selectedDatabaseId ||
      !selectedDatabaseRevisionId ||
      !gradingType
    ) {
      return;
    }

    const { requestCategories, requestRoles } = saveRequestDetails;

    if (mode === 'edit') {
      if (rubricIdParam) {
        setIsSavingRubric(true);

        clientUpdateRubric({
          isAlive,
          userAuthenticatedApiCall,
          onSuccess: (dto) => {
            applyChangesFromDto({
              rubricDto: dto,
              duplicateMode: false,
            });

            clientUpdateRubricStatus({
              rubricId: rubricIdParam,
              onSuccess: () => {
                setIsSavingRubric(false);
                setStatus('published');
                setDirty(false);

                // TODO: Show the toast maybe?
              },
              onError: (newError) => {
                setIsSavingRubric(false);
                const location =
                  getCategoryErrorLocationFromServerError(newError);
                if (location) {
                  setCategoryIntoDraftMode(location.categoryIndex);
                }
                setSaveError(newError ?? 'Unknown error');
              },
              organizationId: selectedOrganizationId,
              databaseId: selectedDatabaseId,
              databaseRevisionId: selectedDatabaseRevisionId,
              userAuthenticatedApiCall,
              isAlive,
              status: 'published',
            });
          },
          onError: (newError) => {
            setIsSavingRubric(false);
            const location = getCategoryErrorLocationFromServerError(newError);
            if (location) {
              setCategoryIntoDraftMode(location.categoryIndex);
            }
            setSaveError(newError ?? 'Unknown error');
          },
          rubricId: rubricIdParam,
          organizationId: selectedOrganizationId,
          databaseId: selectedDatabaseId,
          databaseRevisionId: selectedDatabaseRevisionId,
          rubric: {
            name,
            description,
            integrationDataSetId: dataSetId,
            roles: requestRoles,
            categories: requestCategories,
          },
        });
      }
    } else {
      setIsSavingRubric(true);

      clientCreateRubric({
        isAlive,
        userAuthenticatedApiCall,
        onSuccess: (newDto) => {
          clientUpdateRubricStatus({
            rubricId: newDto.id,
            onSuccess: () => {
              setDirty(false);
              navigate(`/rubrics/edit/${newDto.id}`);
            },
            onError: (newError) => {
              setDirty(false);
              navigate(
                `/rubrics/edit/${newDto.id}?error=${encodeURIComponent(newError ?? 'Unknown error')}`,
              );
            },
            organizationId: selectedOrganizationId,
            databaseId: selectedDatabaseId,
            databaseRevisionId: selectedDatabaseRevisionId,
            userAuthenticatedApiCall,
            isAlive,
            status: 'published',
          });
        },
        onError: (newError) => {
          setIsSavingRubric(false);
          const location = getCategoryErrorLocationFromServerError(newError);
          if (location) {
            setCategoryIntoDraftMode(location.categoryIndex);
          }
          setSaveError(newError ?? 'Unknown error');
        },
        organizationId: selectedOrganizationId,
        databaseId: selectedDatabaseId,
        databaseRevisionId: selectedDatabaseRevisionId,
        rubric: {
          name,
          description,
          integrationDataSetId: dataSetId,
          roles: requestRoles,
          categories: requestCategories,
          gradingType,
        },
      });
    }
  }, [
    applyChangesFromDto,
    dataSetId,
    description,
    getCategoryErrorLocationFromServerError,
    gradingType,
    isAlive,
    mode,
    name,
    navigate,
    rubricIdParam,
    saveRequestDetails,
    selectedDatabaseId,
    selectedDatabaseRevisionId,
    selectedOrganizationId,
    setCategoryIntoDraftMode,
    setDirty,
    userAuthenticatedApiCall,
  ]);

  const handleUnpublish = useCallback(() => {
    if (
      !selectedOrganizationId ||
      !selectedDatabaseId ||
      !selectedDatabaseRevisionId ||
      !gradingType ||
      mode !== 'edit' ||
      !rubricIdParam
    ) {
      return;
    }

    const { requestCategories, requestRoles } = saveRequestDetails;

    setIsSavingRubric(true);

    clientUpdateRubric({
      isAlive,
      userAuthenticatedApiCall,
      onSuccess: (dto) => {
        setSaveError(null);
        setSavedName(name);
        setCreatedAt(dto.createdAt);
        setUpdatedAt(dto.updatedAt);
        clientUpdateRubricStatus({
          rubricId: rubricIdParam,
          onSuccess: () => {
            setIsSavingRubric(false);
            setStatus('draft');
            setDirty(false);

            // TODO: Show the toast maybe?
          },
          onError: (newError) => {
            setIsSavingRubric(false);
            const location = getCategoryErrorLocationFromServerError(newError);
            if (location) {
              setCategoryIntoDraftMode(location.categoryIndex);
            }
            setSaveError(newError ?? 'Unknown error');
          },
          organizationId: selectedOrganizationId,
          databaseId: selectedDatabaseId,
          databaseRevisionId: selectedDatabaseRevisionId,
          userAuthenticatedApiCall,
          isAlive,
          status: 'draft',
        });
      },
      onError: (newError) => {
        setIsSavingRubric(false);
        const location = getCategoryErrorLocationFromServerError(newError);
        if (location) {
          setCategoryIntoDraftMode(location.categoryIndex);
        }
        setSaveError(newError ?? 'Unknown error');
      },
      rubricId: rubricIdParam,
      organizationId: selectedOrganizationId,
      databaseId: selectedDatabaseId,
      databaseRevisionId: selectedDatabaseRevisionId,
      rubric: {
        name,
        description,
        integrationDataSetId: dataSetId,
        roles: requestRoles,
        categories: requestCategories,
      },
    });
  }, [
    dataSetId,
    description,
    getCategoryErrorLocationFromServerError,
    gradingType,
    isAlive,
    mode,
    name,
    rubricIdParam,
    saveRequestDetails,
    selectedDatabaseId,
    selectedDatabaseRevisionId,
    selectedOrganizationId,
    setCategoryIntoDraftMode,
    setDirty,
    userAuthenticatedApiCall,
  ]);

  const handleRestore = useCallback(() => {
    if (
      !selectedOrganizationId ||
      !selectedDatabaseId ||
      !selectedDatabaseRevisionId ||
      mode !== 'edit' ||
      !rubricIdParam
    ) {
      return;
    }

    setIsSavingRubric(true);

    clientUpdateRubricStatus({
      rubricId: rubricIdParam,
      onSuccess: () => {
        setIsSavingRubric(false);
        setDirty(false);
        setStatus('draft');
      },
      onError: (newError) => {
        setIsSavingRubric(false);
        setSaveError(newError ?? 'Unknown error');
      },
      organizationId: selectedOrganizationId,
      databaseId: selectedDatabaseId,
      databaseRevisionId: selectedDatabaseRevisionId,
      userAuthenticatedApiCall,
      isAlive,
      status: 'draft',
    });
  }, [
    isAlive,
    mode,
    rubricIdParam,
    selectedDatabaseId,
    selectedDatabaseRevisionId,
    selectedOrganizationId,
    setDirty,
    userAuthenticatedApiCall,
  ]);

  const [showUnsavedModal, setShowUnsavedModal] = useState(false);

  const shouldBlock = useCallback(() => isDirtyRef.current, []);
  const blocker = useBlocker(shouldBlock);

  useEffect(() => {
    if (blocker.state === 'blocked') {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setShowUnsavedModal(true);
    }
  }, [blocker.state]);

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (!isDirty) return;

      e.preventDefault();
      e.returnValue = '';
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isDirty]);

  return (
    <div className="bg-radial-top-soft">
      <MainLayout showBackToTop={false} scrollableRef={yScrollRef}>
        <Helmet>
          <title>{documentTitle(language.createRubricPageTitle())}</title>
        </Helmet>

        <UserAnalysesOverlay onUserAnalysisCancel={() => {}} />
        <ObservationLoadingAndFailureIndicators
          isLoadingStill={isLoadingObservationOrRubricStill}
          failedToLoadObservation={failedToLoadObservation}
          isDashboardDetailPage={true}
        />

        <UnsavedChangesModal
          show={showUnsavedModal}
          onCancel={() => {
            if (blocker.state === 'blocked') {
              blocker.reset();
            }
            setShowUnsavedModal(false);
          }}
          onConfirmLeave={() => {
            if (blocker.state === 'blocked') {
              blocker.proceed();
            }
            setShowUnsavedModal(false);
          }}
        />

        {!isLoadingObservationOrRubricStill &&
          (rubricAccessLevel === 'manage' || rubricAccessLevel === 'read') &&
          gradingType && (
            <CreateOrEditRubricView
              gradingType={gradingType}
              onNameChange={handleNameChange}
              onDescriptionChange={handleDescriptionChange}
              onIntegrationDataSetIdChange={handleDataSetIdChange}
              onSaveDraft={handleSaveDraft}
              onPublish={handlePublish}
              onUnpublish={handleUnpublish}
              onRestore={handleRestore}
              onRoleNameChange={handleRoleNameChanged}
              onRoleOperationChange={handleRoleOperationChanged}
              onRoleSave={handleRoleSaved}
              onRoleCancel={handleRoleCancelled}
              onRoleBeginEdit={handleRoleBeginEdit}
              onRoleRemove={handleRoleRemove}
              onRoleCreate={handleRoleCreate}
              roles={rolesWithErrors}
              roleLibraryList={roleLibraryList}
              onRoleSelect={handleRoleSelect}
              onAddSelectedRoles={handleAddSelectedRoles}
              onDismissRoleLibrary={handleDismissRoleLibrary}
              isRoleLibraryOpen={isRoleLibraryOpen}
              onCategoryCreate={handleCategoryCreate}
              onCategorySelect={handleCategorySelect}
              onCategoryStartEdit={handleCategoryStartEdit}
              onCategoryUpdateLabel={handleCategoryUpdateLabel}
              onCategoryAddTag={handleCategoryAddTag}
              onCategoryRemoveTag={handleCategoryRemoveTag}
              onCategoryUpdateCriteria={handleCategoryUpdateCriteria}
              onCategoryUpdateWeight={handleCategoryUpdateWeight}
              onCategoryRemoveCriteria={handleCategoryRemoveCriteria}
              onCategoryAddCriteria={handleCategoryAddCriteria}
              onCategoryDelete={handleCategoryRemove}
              onCategoryDuplicate={handleCategoryDuplicate}
              onCategoryCancel={handleCategoryCancel}
              onCategorySave={handleCategorySave}
              categories={categoriesWithErrors}
              categoryLibraryList={categoryLibraryList}
              onAddSelectedCategories={handleAddSelectedCategories}
              onDismissCategoryLibrary={handleDismissCategoryLibrary}
              isCategoryLibraryOpen={isCategoryLibraryOpen}
              status={status}
              name={name}
              savedName={savedName}
              description={description}
              integrationDataSetId={dataSetId}
              possibleTags={possibleTags}
              disableSave={disableSave}
              possibleIntegrationDataSets={possibleDataSets}
              createdAt={createdAt}
              updatedAt={updatedAt}
              serverError={serverError}
              isReadOnlyMode={isReadOnlyMode}
              isArchiveMode={isArchiveMode}
              canManage={rubricAccessLevel === 'manage'}
            />
          )}
      </MainLayout>
    </div>
  );
};

export const CreateRubricPageWrapper: React.FC<{
  mode: 'create' | 'edit' | 'duplicate';
}> = ({ mode }) => {
  const { rubricId, gradingType } = useParams();
  const key = useMemo(() => {
    return `${mode}-${rubricId}-${gradingType}`;
  }, [gradingType, mode, rubricId]);
  return (
    <CreateRubricPage
      mode={mode}
      rubricIdParam={rubricId}
      gradingTypeParam={gradingType}
      key={key}
    />
  );
};
